import PropTypes from 'prop-types';
import { forbidExtraProps } from 'airbnb-prop-types';
import { withStyles, withTheme } from '@material-ui/styles';
import classNames from 'classnames';
import FocusTrap from 'focus-trap-react';
import { ReactComponent as Cross } from '../assets/images/cross_icon.svg';
import ModalPortal from './ModalPortal';

const styles = theme => ({
  modalBackdrop: {
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(40, 51, 51, 0.6)',
    position: 'fixed',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    top: 0,
    left: 0,
    padding: `${theme.spacing.s24}px`,
    // The highest z-index known at that time is the checkout ticket at 1500 (not taking into 999999 index instances)
    zIndex: 1501, // Would be great to clean this number once we have a proper z-index strategy
  },
  modalBody: {
    padding: `${theme.spacing.s72}px`,
    paddingTop: `${theme.spacing.s96}px`,
    width: '100%',
    maxWidth: '650px',
    height: '100%',
    maxHeight: '660px',
    overflow: 'auto',
    backgroundColor: theme.palette.backgrounds.white,
    position: 'relative',
    [theme.breakpoints.match('sm')]: {
      maxWidth: '360px',
      padding: `${theme.spacing.s24}px`,
      paddingTop: `${theme.spacing.s72}px`,
    },
  },
  closeModalButton: {
    width: theme.spacing.s24,
    height: theme.spacing.s24,
    position: 'absolute',
    right: theme.spacing.s24,
    top: theme.spacing.s24,
    appearance: 'none',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    margin: 0,
  },
});

const BasicModal = ({
  className,
  isOpen,
  onClose,
  classes,
  children,
  theme,
  dataFrom,
  dataClick,
  dataTestId,
  isFocusTrapActive,
}) => (
  <ModalPortal isOpen={isOpen}>
    <FocusTrap active={isFocusTrapActive}>
      <div
        className={classes.modalBackdrop}
        onClick={onClose}
        onKeyDown={e => {
          if (e.which === 27 || e.keyCode === 27) {
            onClose();
          }
        }}
        role="presentation"
      >
        <div
          className={className ? classNames(classes.modalBody, className) : classes.modalBody}
          onClick={e => {
            // This is necessary or clicking on the modal will close it
            e.stopPropagation();
          }}
          role="presentation"
        >
          {children}
          <button
            aria-label="close"
            className={classes.closeModalButton}
            data-click={dataClick}
            data-from={dataFrom}
            data-testid={dataTestId}
            onClick={onClose}
            type="button"
          >
            <Cross height={theme.spacing.s24} width={theme.spacing.s24} />
          </button>
        </div>
      </div>
    </FocusTrap>
  </ModalPortal>
);

BasicModal.propTypes = forbidExtraProps({
  className: PropTypes.string,
  dataFrom: PropTypes.string,
  dataClick: PropTypes.string,
  children: PropTypes.node.isRequired,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  theme: PropTypes.shape({
    spacing: PropTypes.shape({ s24: PropTypes.number.isRequired }).isRequired,
  }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  isFocusTrapActive: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  dataTestId: PropTypes.string,
});

BasicModal.defaultProps = {
  className: null,
  dataFrom: 'modal',
  dataClick: 'close',
  dataTestId: 'subscription-modal-closer',
  isFocusTrapActive: true,
};

export default withStyles(styles)(withTheme(BasicModal));
